/**
 * ORTEX Advanced price + first-month offer for a signed-out visitor.
 *
 * Signed-out visitors have no Stripe customer, so the in-app subscription
 * payload (`/API/v2/user/new_subscription`) carries no plans for them. The
 * backend's public pricing endpoint (`/pay/api/public_pricing/`, built for
 * ortex-public) prices plans in the visitor's currency with the same IP lookup
 * the checkout uses (`get_currency_for_request`), so the price shown here is
 * the price the checkout will show.
 *
 * The first-month offer is TRYAI: 7.99 in each currency (79 kr in SEK), the
 * same promotion the checkout's exit offer and the public.ortex.com ad landing
 * pages use. The checkout resolves the generic code to the currency variant
 * (`get_promotion_code_details` appends the customer currency). The amounts
 * live in Stripe, not in an endpoint a visitor can call, so they are mirrored
 * here (as they are in TEASER_BY_CURRENCY in Payment.tsx); change them together.
 *
 * Why 7.99 and not a higher first month: past cohorts show the trial price
 * moves take-up, not the rate at which trials go on to pay full price (31-39%
 * at 7.99, 33-38% at $29 with a third of the volume), so a higher entry price
 * mostly cuts the number of full-price customers. A different price up front
 * would also be undercut by the 7.99 exit offer on closing the checkout.
 *
 * Nothing here changes what the checkout charges: it only labels the CTA.
 */

export type OfferCurrency = "usd" | "eur" | "gbp" | "cad" | "sek";

export interface AdvancedOffer {
    currency: OfferCurrency;
    /** e.g. "$7.99", "79 kr" */
    offerPrice: string;
    /** e.g. "$149", "1,490 kr" */
    fullPrice: string;
    /** Register + checkout link with the offer preselected (monthly Advanced). */
    offerHref: string;
}

export const OFFER_PROMO_CODE = "TRYAI";
export const OFFER_HREF = `/register?promo=${OFFER_PROMO_CODE}&t=monthly&v=advanced&checkout=true`;

const OFFER_AMOUNT: Record<OfferCurrency, number> = { usd: 7.99, eur: 7.99, gbp: 7.99, cad: 7.99, sek: 79 };

// Fallback only, used when the pricing endpoint cannot be reached. Same values
// as the checkout's teaser table (TEASER_BY_CURRENCY in Payment.tsx).
const FULL_PRICE_FALLBACK: Record<OfferCurrency, number> = { usd: 149, eur: 149, gbp: 119, cad: 199, sek: 1490 };

export const formatPrice = (currency: OfferCurrency, amount: number): string => {
    // Under 1,000 there is no grouping to do, so no Intl: DEFAULT_OFFER is built while the app's
    // bundle loads, and the page's first Intl call (about 120 ms on a phone) landed in that long task.
    const n =
        Math.abs(amount) < 1000 ? (Number.isInteger(amount) ? String(amount) : amount.toFixed(2)) : Number.isInteger(amount) ? amount.toLocaleString("en-US") : amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    switch (currency) {
        case "eur":
            return `€${n}`;
        case "gbp":
            return `£${n}`;
        case "cad":
            return `CA$${n}`;
        case "sek":
            return `${n} kr`;
        default:
            return `$${n}`;
    }
};

const isOfferCurrency = (c: string): c is OfferCurrency => c in OFFER_AMOUNT;

export const buildOffer = (currency: OfferCurrency, fullAmount?: number): AdvancedOffer => ({
    currency,
    offerPrice: formatPrice(currency, OFFER_AMOUNT[currency]),
    fullPrice: formatPrice(currency, fullAmount ?? FULL_PRICE_FALLBACK[currency]),
    offerHref: OFFER_HREF,
});

/** USD until the visitor's currency is known (the ads are bought in USD wording). */
export const DEFAULT_OFFER = buildOffer("usd");

let inflight: Promise<AdvancedOffer> | null = null;

/**
 * The visitor's offer, fetched once per page load. Never rejects: any failure
 * resolves to the USD default so the panel always has something to show.
 */
export const loadAdvancedOffer = (): Promise<AdvancedOffer> => {
    if (inflight) return inflight;
    const base = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/$/, "");
    inflight = fetch(`${base}/pay/api/public_pricing/`, { credentials: "omit" })
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((data) => {
            // The API camel-cases keys (currencyCode); accept snake_case too.
            const code = String(data?.currencyCode ?? data?.currency_code ?? "usd").toLowerCase();
            const currency: OfferCurrency = isOfferCurrency(code) ? code : "usd";
            const prices: any[] = data?.platform?.advanced?.prices ?? [];
            const monthly = prices.find((p) => p?.recurring?.interval === "month" && String(p?.currency).toLowerCase() === currency);
            const amount = Number(monthly?.amount);
            return buildOffer(currency, Number.isFinite(amount) && amount > 0 ? amount : undefined);
        })
        .catch(() => {
            inflight = null; // let a later mount retry
            return DEFAULT_OFFER;
        });
    return inflight;
};
